import { LinkCell } from 'components/link-cell';
import { Fragment, useCallback, useMemo } from 'react';
import { useStore } from '@tanstack/react-form';
import {
  DataTable,
  type ColumnType,
  type DataTableColumnDef,
  type TableState,
} from 'components/data-table';
import { Link } from 'components/link';
import { formatTitleRiskValue } from 'pages/crm-tracts-subtracts/utils';
import {
  subtractCoverageSuffix,
  tractRowCoverageLabel,
} from 'utils/subtract-coverage';
import { SlotAllocationExpandedRow } from './slot-allocation-expanded-row';
import { DpsAreaCell } from './dps-area-cell';
import { unvaluedReasonColumn } from './unvalued-reason-column-utils';
import { useMasterDataView, type WorkingConfigForm } from './hooks';
import {
  DECIMAL_VARIABLE,
  EMPTY,
  linkCellRenderer,
  typeCurveAreaLink,
  locateColumn,
  MASTER_DATA_PAGE_SIZES,
  slotIdLink,
  toTableFilters,
  useMasterDataVisibleColumns,
  wellStatusColumn,
} from './utils';
import { unitAndInterestColumns } from './slot-column-utils';
import {
  subtractCountColumns,
  SUBTRACT_COUNT_COLUMNS_HIDDEN,
} from './subtract-count-column-utils';
import { VALUED_FILTER_META, valuedCell } from './valued-column-utils';
import type { MasterDataViewProps, SlotAllocationRow } from './types';

export interface SlotAllocationViewProps extends MasterDataViewProps {
  workingConfig: WorkingConfigForm;
}

/**
 * DataTable needs an index signature on its row type; `SlotAllocationRow`
 * (= `TractNRI`, an interface) doesn't carry one, so intersect it here.
 */
type SlotAllocationTableRow = SlotAllocationRow & Record<string, unknown>;

type SlotAllocationColumnKey = keyof SlotAllocationRow & string;

/**
 * Column rendered by DataTable's built-in `meta.type` formatter (no custom cell).
 *
 * @param {string} accessorKey - The `SlotAllocationRow` field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @param {ColumnType} type - The DataTable column type driving formatting + alignment.
 * @return {object} The DataTable column definition.
 */
function column(
  accessorKey: SlotAllocationColumnKey,
  header: string,
  size: number,
  type: ColumnType
): DataTableColumnDef<SlotAllocationTableRow> {
  // Date/datetime columns opt into the inline Start/End range filter via
  // `dateFilterLocalTime` — the same local-time single-range picker AssetView
  // uses, which repaints the End bound reliably (the two-single-pickers default
  // left End blank until a remount). Every other type keeps a bare `{ type }`.
  const isDate = type === 'date' || type === 'datetime';
  return {
    accessorKey,
    header,
    size,
    meta: isDate ? { type, dateFilterLocalTime: true } : { type },
  };
}

/**
 * A scenario-area column: the area NAME linked to the scenario whose area it is,
 * opening in a new tab. Spreads column() so sorting and the text filter behave
 * exactly like every other text column; only the cell changes. linkCellRenderer
 * falls back to plain text when the href is missing (an eval captured before
 * hrefs were recorded).
 *
 * @param {string} accessorKey - The area-name field.
 * @param {string} hrefKey - The paired href field.
 * @param {string} header - The column header label.
 * @param {number} size - The column width in px.
 * @return {object} The DataTable column definition.
 */
function areaColumn(
  accessorKey: SlotAllocationColumnKey,
  hrefKey: keyof SlotAllocationTableRow,
  header: string,
  size: number
): DataTableColumnDef<SlotAllocationTableRow> {
  return {
    ...column(accessorKey, header, size, 'text'),
    cell: (info) =>
      linkCellRenderer(
        info.getValue<string>(),
        info.row.original[hrefKey] as string | null | undefined
      ),
  };
}

/**
 * Slot Allocation columns ported from `SLOT_ALLOCATIONS_COLUMNS` (FullNRITable),
 * minus the 10 deferred edit columns. Order matches the source — tract-level
 * fields first (CRM Tract / Account), then the slot fields, then tract detail.
 * Text / integer / currency / decimal columns render from `meta.type`; only
 * links, the NRI/WI tooltips, the Valued label, and the title-risk labels carry
 * a custom `cell`. Every column is sortable server-side (Unvalued Reason sorts
 * only once the BE projects it — a no-op until then).
 */
const BASE_SLOT_ALLOCATION_COLUMNS: DataTableColumnDef<SlotAllocationTableRow>[] =
  [
    {
      accessorKey: 'crmTract',
      header: 'CRM Tract',
      size: 200,
      // A Tract the package holds in part says how much of it the package holds.
      cell: (info) => {
        const value = info.getValue<string>();
        const { crmTractSalesforceId } = info.row.original;
        if (!value || !crmTractSalesforceId) {
          return value || EMPTY;
        }
        const held = tractRowCoverageLabel(info.row.original);
        return (
          <>
            <Link
              to={`/crm/tracts_and_subtracts/${encodeURIComponent(
                crmTractSalesforceId
              )}/edit`}
              openInNewTab
            >
              {value}
            </Link>
            {subtractCoverageSuffix(held)}
          </>
        );
      },
    },
    ...subtractCountColumns<SlotAllocationTableRow>(),
    {
      accessorKey: 'crmAccount',
      header: 'CRM Account',
      size: 180,
      cell: (info) =>
        linkCellRenderer(
          info.getValue<string>(),
          info.row.original.crmAccountHref
        ),
    },
    {
      accessorKey: 'slotId',
      header: 'Slot ID',
      size: 140,
      cell: (info) =>
        linkCellRenderer(
          info.getValue<string>(),
          slotIdLink(info.row.original)
        ),
    },
    {
      // Read-only in the Slot Allocation view (per-cell edit is Slot-view only).
      // Mirrors the Slot view's display: links only for type-curve sources that
      // carry an href, plain text otherwise.
      accessorKey: 'volumeSourceId',
      header: 'Volume Source',
      size: 200,
      cell: (info) => {
        const row = info.row.original;
        const isTypeCurve = row.volumeSourceType === 'type_curve';
        return linkCellRenderer(
          row.volumeSourceName ?? row.volumeSourceId ?? undefined,
          isTypeCurve ? row.volumeSourceHref : undefined
        );
      },
    },
    column('forecastStatus', 'Forecast Status', 150, 'text'),
    column('forecastDate', 'Forecast Date', 140, 'date'),
    column('dcaScenario', 'DCA Scenario', 150, 'text'),
    areaColumn('expenseArea', 'expenseAreaHref', 'Expense Area', 150),
    column('slotLength', 'Slot Length', 130, 'integer'),
    {
      ...column('typeCurveAreaName', 'Type Curve Area', 160, 'text'),
      // The AREA has no page (/type_curve_areas/:id is JSON-only), so the cell
      // links to the type CURVE that priced the slot. See typeCurveAreaLink.
      cell: (info) => (
        <LinkCell
          value={info.getValue<string>()}
          href={typeCurveAreaLink(info.row.original)}
        />
      ),
    },
    // Scenario-area display names (parity with the legacy slot-allocations table);
    // populated in calc mode, blank in package/live mode (area assignment is
    // calc-scoped).
    {
      ...areaColumn('dpsArea', 'dpsAreaHref', 'DPS Area', 160),
      cell: (info) => <DpsAreaCell row={info.row.original} />,
    },
    areaColumn('taxArea', 'taxAreaHref', 'Tax Area', 160),
    areaColumn(
      'differentialArea',
      'differentialAreaHref',
      'Differential Area',
      170
    ),
    areaColumn(
      'discountRateArea',
      'discountRateAreaHref',
      'Discount Rate Area',
      180
    ),
    column('typeCurveLateralLength', 'TC Lateral Length', 150, 'integer'),
    {
      // Read-only AFE Source (per-cell edit is Slot-view only). Links via the
      // BE-provided href when present, plain text otherwise.
      accessorKey: 'afeSourceId',
      header: 'AFE Source',
      size: 180,
      cell: (info) =>
        linkCellRenderer(
          info.getValue<string | number>(),
          info.row.original.afeSourceHref
        ),
    },
    column('afeWellName', 'AFE Well Name', 180, 'text'),
    column(
      'slotContingenciesCosts',
      'Slot Contingencies Costs',
      200,
      'currency'
    ),
    column('slotFacilitiesCosts', 'Slot Facilities Costs', 180, 'currency'),
    column('slotDrillingCosts', 'Slot Drilling Costs', 170, 'currency'),
    column('slotCompletionCosts', 'Slot Completion Costs', 180, 'currency'),
    column('slotOtherCosts', 'Slot Other Costs', 150, 'currency'),
    ...unitAndInterestColumns<SlotAllocationTableRow>(),
    column('allocationMethod', 'Allocation Method', 160, 'text'),
    {
      accessorKey: 'tractNetAcres',
      header: 'Tract Net Acres',
      size: 160,
      meta: DECIMAL_VARIABLE,
    },
    column('netAcreageUnits', 'Net Acreage Units', 150, 'text'),
    {
      accessorKey: 'tractGrossAcres',
      header: 'Tract Gross Acres',
      size: 170,
      meta: DECIMAL_VARIABLE,
    },
    column('surveyArea', 'Survey Area', 120, 'text'),
    column('stateAbbreviation', 'State', 90, 'text'),
    column('quarterCall', 'Quarter Call', 120, 'text'),
    column('instrumentType', 'Instrument Type', 130, 'text'),
    column('interestType', 'Interest Type', 140, 'text'),
    column('depthRange', 'Depth Range', 140, 'text'),
    column('unitSource', 'Unit Source', 140, 'text'),
    column('unitDevelopmentType', 'Unit Development Type', 170, 'text'),
    {
      accessorKey: 'wellName',
      header: 'Well Name',
      size: 180,
      cell: (info) =>
        linkCellRenderer(
          info.getValue<string>(),
          slotIdLink(info.row.original)
        ),
    },
    wellStatusColumn<SlotAllocationTableRow>(),
    column('typeCurveStatus', 'Type Curve Status', 150, 'text'),
    column('operator', 'Operator', 180, 'text'),
    column('holeDirection', 'Hole Direction', 140, 'text'),
    column('formation', 'Formation', 160, 'text'),
    column('basin', 'Basin', 130, 'text'),
    column('tier', 'Tier', 100, 'text'),
    column('trueVerticalDepthFeet', 'TVD (ft)', 120, 'integer'),
    // Slot number is an identifier, not a quantity — render as text (no commas).
    column('slotNumber', 'Slot #', 100, 'text'),
    // Date cluster order mirrors the Properties (slots) view:
    // permit, spud, rigRelease, completion, firstProduction, shutIn. Read-only
    // here (per-cell date edit is Slot-view only), so all use the plain `date`
    // column — unlike the Properties view where permit/spud/firstProduction are
    // editable override columns.
    column('permitDate', 'Permit Date', 140, 'date'),
    column('spudDate', 'Spud Date', 130, 'date'),
    column('rigReleaseDate', 'Rig Release Date', 150, 'date'),
    column('completionDate', 'Completion Date', 150, 'date'),
    column('firstProductionDate', 'First Production Date', 180, 'date'),
    column('shutInDate', 'Shut In Date', 140, 'date'),
    {
      accessorKey: 'valued',
      header: 'Valued',
      size: 100,
      // INS-12871 — shared Yes/No filter meta + cell (VALUED_FILTER_META / valuedCell in
      // valued-column-utils, same as Properties). `enableColumnFilter` is injected by the component below:
      // the filter is eval-scoped, so it's offered only in calc mode (a `valued` filter with
      // no calculationId 422s — requires_eval_calc).
      meta: VALUED_FILTER_META,
      cell: valuedCell,
    },
    {
      accessorKey: 'titleQuality',
      header: 'Title Quality',
      size: 130,
      cell: (info) =>
        formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
    },
    {
      accessorKey: 'titleRiskFactor',
      header: 'Title Risk Factor',
      size: 150,
      cell: (info) =>
        formatTitleRiskValue(info.getValue<string | null>(), EMPTY),
    },
  ];

/** Where the table keeps the user's column customizations. */
const PERSISTENCE_KEY = 'eval3-new-master-data-slot-allocation';

/**
 * Every column wraps long content (row height grows to fit) — `meta.wrap` is
 * injected uniformly while preserving each column's existing `meta`.
 */
// Every column wraps long content and skips filter autocomplete.
const COLUMN_META = { wrap: true, disableFilterAutocomplete: true };

const SLOT_ALLOCATION_COLUMNS: DataTableColumnDef<SlotAllocationTableRow>[] =
  BASE_SLOT_ALLOCATION_COLUMNS.map((col) => ({
    ...col,
    meta: { ...col.meta, ...COLUMN_META },
  }));

/**
 * Slot Allocation view of the Master Data Panel. Renders the ported Slot
 * Allocation columns (read-only in P1 — the 10 edit columns are deferred) on
 * `components/data-table` with server-driven pagination, sort, and search via
 * `useMasterDataView`. Row expansion shows the subtract sub-table (values-only).
 *
 * ── A PENDING EDIT SHOWS HERE TOO ──────────────────────────────────────────────────────
 *
 * The working config's pending `slotOverrides` ride this view's read exactly as they ride
 * the Slot view's: master_data resolves them for `allocations` as well as `slots`, and an
 * allocation row carries the slot's `slotId`, so the server applies each op to every tract
 * row of its slot. Without them this grid showed the committed value beside a Slot view
 * showing the pending one.
 *
 * The limit is the Slot view's too: `slotOverrides` stays out of the query key, so an edit
 * made while this view is mounted shows on its next fetch, not at once. Schedule-dialog
 * dates are not here either — the Slot view overlays those client-side, and this does not.
 *
 * @param {SlotAllocationViewProps} props - The page identity, the view-selector dropdown slot, and the override form.
 * @return {JSX.Element} The Slot Allocation data table.
 */
export function SlotAllocationView({
  packageId,
  calculationId,
  viewDropdown,
  workingConfig,
}: SlotAllocationViewProps) {
  const slotOverrides = useStore(
    workingConfig.form.store,
    (state) => state.values.slotOverrides
  );
  // The form's state updates synchronously on append, so a refetch reads the log as it
  // is now rather than as it was at the last render.
  const { form } = workingConfig;
  const readSlotOverrides = useCallback(
    () => form.state.values.slotOverrides,
    [form]
  );
  const {
    rows,
    totalCount,
    isFetching,
    error,
    pagination,
    onPaginationChange,
    onSortChange,
    onGlobalSearchChange,
    onColumnFilterChange,
  } = useMasterDataView({
    view: 'allocations',
    packageId,
    calculationId,
    slotOverrides,
    readSlotOverrides,
    // INS-12871 — the Valued filter is eval-scoped (user-set here, no default seed), so re-seed
    // the wire filter (and re-key the DataTable below) when the eval changes in place, clearing
    // a stale `valued` that would 422.
    resetTableFiltersOnCalcChange: true,
  });

  // Prepend the shared "show on map" pin column to the ported columns. The pin
  // cell reads/writes `locatedRowAtom` itself, so that column takes no args. The
  // Unvalued Reason column is built here, after Valued, for this view's `packageId`.
  //
  // INS-12871 — the Valued filter is eval-scoped, so enable it only in calc mode (a `valued`
  // filter with no calculationId 422s — requires_eval_calc). It mirrors the Properties view
  // and the dataset-filter accordion, which also disable eval-scoped attributes off a calc.
  const valuedFilterEnabled = calculationId !== null;
  const columns = useMemo(
    () => [
      locateColumn<SlotAllocationTableRow>(),
      ...SLOT_ALLOCATION_COLUMNS.flatMap((col) => {
        if (!('accessorKey' in col) || col.accessorKey !== 'valued') {
          return [col];
        }
        // Unvalued Reason follows Valued, built for this view's `packageId` (an exclusion links
        // to that package's edit page, INS-12687).
        const reasonColumn =
          unvaluedReasonColumn<SlotAllocationTableRow>(packageId);
        return [
          { ...col, enableColumnFilter: valuedFilterEnabled },
          { ...reasonColumn, meta: { ...reasonColumn.meta, ...COLUMN_META } },
        ];
      }),
    ],
    [packageId, valuedFilterEnabled]
  );

  // Publish the columns this table shows, in their on-screen order, to the shared atom so the
  // content export's "Current View" preset exports the layout on screen.
  const handleVisibleColumnsChange = useMasterDataVisibleColumns(
    'allocations',
    columns
  );

  // Inline column filters (INS-11272): translate the DataTable's ColumnFiltersState
  // to the `tableFilters` wire shape and hand it up with the snapshot's reset page.
  const onFilterChange = useCallback(
    (state: TableState) =>
      onColumnFilterChange(
        toTableFilters(state.columnFilters, columns, 'allocations'),
        state.page
      ),
    [onColumnFilterChange, columns]
  );

  return (
    <DataTable<SlotAllocationTableRow>
      // INS-12871 — re-key on the calc identity so the table re-seeds its inline column filters
      // whenever `useMasterDataView` resets the wire half on a calc change; the eval-scoped
      // `valued` filter can't then ride a run eval → package/draft switch and 422. Matches the
      // Properties view; keepPreviousData + controlled pagination survive a table remount.
      key={calculationId ?? 'draft'}
      data={rows as SlotAllocationTableRow[]}
      columns={columns}
      // A row is a slot×tract allocation; the BE `id` is unique per allocation
      // row (currently the `slotId::tractId` composite).
      getRowId={(row) => String(row.id)}
      // `isFetching` so the refetch loading state shows under `keepPreviousData`.
      isLoading={isFetching}
      error={error ?? null}
      // Controlled pagination: useMasterDataView is the single page owner, so a
      // filter-driven reset to page 1 stays in sync with the pager display.
      controlledState={{ pagination }}
      persistenceKey={PERSISTENCE_KEY}
      initialTableState={{ columnVisibility: SUBTRACT_COUNT_COLUMNS_HIDDEN }}
      config={{
        columnFilters: true,
        columnReordering: true,
        // Center every column header label (data cells keep their own alignment).
        centerHeaders: true,
      }}
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      pageSizes={MASTER_DATA_PAGE_SIZES}
      onPaginationChange={onPaginationChange}
      onSortChange={onSortChange}
      onGlobalSearchChange={onGlobalSearchChange}
      onFilterChange={onFilterChange}
      onVisibleColumnsChange={handleVisibleColumnsChange}
      renderRowSubComponent={(row) => (
        <SlotAllocationExpandedRow row={row.original} />
      )}
      getRowCanExpand={(row) => (row.original.subtracts?.length ?? 0) > 0}
      searchPlaceholder="Search property allocations..."
      leftToolbarActions={[
        <Fragment key="view-dropdown">{viewDropdown}</Fragment>,
      ]}
    />
  );
}
